/**
 * StatusDot — the 12px / 14px colored circle used in every equipment card row.
 *
 * Single source of truth for the dot's appearance. Variants map to FOM's
 * exact colors so there is zero visual drift after refactor.
 *
 * Variants:
 *   green   = available     #22c55e (active) / #d1d5db (inactive)
 *   orange  = assigned/out  #f97316 (active) / #d1d5db (inactive)
 *   red     = not available #ef4444 (active) / #d1d5db (inactive)
 *   blue    = on duty       #3b82f6 (active) / #9ca3af (inactive)
 *   gray    = neutral       #d1d5db
 */
import type { CSSProperties } from 'react'

export type DotVariant = 'green' | 'orange' | 'red' | 'blue' | 'gray'

interface StatusDotProps {
  variant: DotVariant
  active?: boolean
  size?: 12 | 14
  style?: CSSProperties
}

const ACTIVE_COLORS: Record<DotVariant, string> = {
  green:  '#22c55e',
  orange: '#f97316',
  red:    '#ef4444',
  blue:   '#3b82f6',
  gray:   '#9ca3af',
}

const INACTIVE = '#d1d5db'

export default function StatusDot({ variant, active = true, size = 14, style }: StatusDotProps) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: active ? ACTIVE_COLORS[variant] : INACTIVE,
        ...style,
      }}
    />
  )
}

/** Label color helper — matches FOM's lc() function in EquipmentPanel. */
export function dotLabelColor(variant: DotVariant, active: boolean): string {
  if (!active) return '#9ca3af'
  switch (variant) {
    case 'green':  return '#15803d'
    case 'orange': return '#9a3412'
    case 'red':    return '#b91c1c'
    case 'blue':   return '#1d4ed8'
    case 'gray':   return '#6b7280'
  }
}
